import {
    type GoalIconPosition,
    type OnboardingMoneyMapHighlightedBox,
    OnboardingMoneyMapPreview,
    type OnboardingMoneyMapSystemBoxPositions,
} from '@fruitful/money-map';
import {
    type DebtAhaResult,
    type Guide,
    ResultSlide,
    type SavingsAhaResult,
} from '@fruitful/types';
import { LinearGradient } from 'expo-linear-gradient';
import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    type LayoutChangeEvent,
    type ScrollView as RNScrollView,
} from 'react-native';
import Animated, {
    Easing,
    interpolate,
    runOnJS,
    useAnimatedStyle,
    useSharedValue,
    withDelay,
    withSpring,
    withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    Animations,
    ArrowLeft,
    ArrowRight,
    BodyLargeEmphasized,
    BodyLargeRegular,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    ChartLineIcon,
    CloseButton,
    FontFamilyName,
    FruitfulLogo,
    GRADIENT_COLOR_BOTTOM,
    GRADIENT_COLOR_TOP,
    H5Emphasized,
    Icon,
    IconButton,
    Image,
    isNonMobileWeb,
    isWeb,
    LabelRegular,
    MoneyMapIcon,
    MoneySystemIcon,
    ScrollView,
    Spinner,
    SpinnerSize,
    Text,
    useThemeColors,
    useWindowDimensions,
    View,
    XStack,
    YStack,
} from 'smoothie';
import { useDebouncedCallback } from 'use-debounce';

import { ConversationalMMAnalyticsEvent } from '../analytics/events';
import {
    ConversationalMMResultsScreenEvent,
    MoneyMapStep,
    NO_DATE_HEADLINE,
} from '../constants';
import { useConversationalMMContext } from '../context/ConversationalMMContext';
import { useGetRuleSets } from '../hooks/useGetRuleSets';
import {
    CULTIVATING_FADE_MS,
    CULTIVATING_HOLD_MS,
    CultivatingScreen,
} from './CultivatingScreen';
// HowSystemWorksContent (the standalone full-page version) is kept around in
// case we revert; the System step now weaves HowSystemWorksBody into its left
// column, gated by the `explainer` state.
import {
    EXPLAINER_COLLAPSE_BASE_MS,
    HowSystemWorksBody,
} from './HowSystemWorksContent';

const DEFAULT_VIEWPORT =
    'width=device-width, initial-scale=1, shrink-to-fit=no';

enum Slide {
    'AhaMoment',
    'MoneyMap',
}

const useIntroText = (ahaResult: DebtAhaResult | SavingsAhaResult): string => {
    if (ahaResult.type === 'debt' && ahaResult.debtFreeDate !== '') {
        const suffix = ahaResult.type === 'debt' ? ' be\u2026' : '\u2026';
        return `Your Money Map is ready! Based on everything you\u2019ve told us, we estimate that you could${suffix}`;
    }
    return 'Your Money Map is ready!';
};

/**
 * The chat page (OnboardingChatShell) portals a position:fixed green
 * gradient into document.body and sets viewport-fit=cover. Because
 * Expo Router uses a default Stack navigator for the get-started
 * directory, the chat page stays mounted when results is pushed on
 * top — the portal and viewport-fit persist.
 *
 * With viewport-fit=cover, Safari 26's Liquid Glass bars are
 * translucent and the green gradient bleeds through.
 *
 * This hook resets viewport-fit so bars become solid, hides stale
 * portaled fixed elements from document.body so nothing green shows
 * through, and sets body background-color for the bar color fallback.
 */
const useClearChatPageGradient = () => {
    const isMobileWeb = isWeb && !isNonMobileWeb;

    useLayoutEffect(() => {
        if (!isMobileWeb || typeof document === 'undefined') {
            return undefined;
        }

        const viewportMetas = document.querySelectorAll(
            'meta[name="viewport"]',
        );
        viewportMetas.forEach((meta) => {
            meta.setAttribute('content', DEFAULT_VIEWPORT);
        });

        const hiddenElements: HTMLElement[] = [];
        const bodyChildren = Array.from(document.body.children);
        for (const child of bodyChildren) {
            if (
                child.id === 'root' ||
                child.tagName === 'SCRIPT' ||
                child.tagName === 'STYLE' ||
                child.tagName === 'LINK' ||
                child.tagName === 'META'
            ) {
                continue;
            }
            const el = child as HTMLElement;
            const computed = window.getComputedStyle(el);
            if (computed.position === 'fixed') {
                el.style.display = 'none';
                hiddenElements.push(el);
            }
        }

        document.body.style.backgroundColor = '#ffffff';

        return () => {
            for (const el of hiddenElements) {
                el.style.display = '';
            }
        };
    }, [isMobileWeb]);
};

// Desktop tab switcher (visual). Icons chosen to echo the Figma: money-map,
// automated system, and debt-free timeline.
const MONEY_MAP_TABS = [
    { step: MoneyMapStep.Split, icon: MoneyMapIcon, color: '$leaf' },
    { step: MoneyMapStep.System, icon: MoneySystemIcon, color: '$water' },
    { step: MoneyMapStep.Timeline, icon: ChartLineIcon, color: '$petal' },
];

// --- Summary (terminal) page: a fanned stack of Guide portraits + CTA ---
const GUIDE_LAYER_OFFSETS = [0, 69, 111, 127];
const GUIDE_DARKEN = [0, 0.13, 0.27, 0.4];
const GUIDE_LAYER_COUNT = 4;
const GUIDE_STAGGER_MS = 125;
const GUIDE_CARD_COUNT = 7;

const pickGuides = (guides: Guide[]): Guide[] => {
    if (guides.length === 0) return Array.from({ length: GUIDE_CARD_COUNT });
    if (guides.length >= GUIDE_CARD_COUNT) {
        const shuffled = [...guides].sort(() => Math.random() - 0.5);
        return shuffled.slice(0, GUIDE_CARD_COUNT);
    }
    const result: Guide[] = [];
    for (let i = 0; i < GUIDE_CARD_COUNT; i++) {
        result.push(guides[i % guides.length] as Guide);
    }
    return result;
};

const GuideCard = ({
    index,
    guide,
    progress,
}: {
    index: number;
    guide: Guide | undefined;
    progress: Animated.SharedValue<number>;
}) => {
    const distFromCenter = Math.abs(index - 3);
    const scale = 1 - distFromCenter * 0.12;
    const zIndex = 4 - distFromCenter;
    const sign = index < 3 ? -1 : index > 3 ? 1 : 0;
    const offset = (GUIDE_LAYER_OFFSETS[distFromCenter] ?? 0) * sign;
    const darkOverlay = GUIDE_DARKEN[distFromCenter] ?? 0.4;

    const animStyle = useAnimatedStyle(() => ({
        opacity: progress.value,
        transform: [{ scale }, { translateY: (1 - progress.value) * 20 }],
    }));

    return (
        <Animated.View
            style={[
                {
                    position: 'absolute',
                    left: 158 + offset - 48,
                    top: 0,
                    zIndex,
                },
                animStyle,
            ]}
        >
            <Image
                source={{
                    uri: guide?.profileImageUrl ?? '',
                }}
                borderRadius={16}
                width={96}
                height={120}
            />
            {darkOverlay > 0 && (
                <View
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        borderRadius: 16,
                        backgroundColor: `rgba(0,0,0,${darkOverlay})`,
                    }}
                />
            )}
        </Animated.View>
    );
};

const GuideCardStack = ({ guides }: { guides: Guide[] }) => {
    const selectedGuides = useMemo(() => pickGuides(guides), [guides]);

    const p0 = useSharedValue(0);
    const p1 = useSharedValue(0);
    const p2 = useSharedValue(0);
    const p3 = useSharedValue(0);
    const layerProgress = useMemo(() => [p0, p1, p2, p3], [p0, p1, p2, p3]);

    useEffect(() => {
        for (let layer = GUIDE_LAYER_COUNT - 1; layer >= 0; layer--) {
            const reverseIndex = GUIDE_LAYER_COUNT - 1 - layer;
            const sv = layerProgress[layer];
            if (sv != null) {
                sv.value = withDelay(
                    reverseIndex * GUIDE_STAGGER_MS,
                    withSpring(1, {
                        damping: 14,
                        stiffness: 60,
                        mass: 0.8,
                    }),
                );
            }
        }
    }, []);

    return (
        <View style={{ width: 316, height: 120 }}>
            {selectedGuides.map((guide, i) => {
                const progress = layerProgress[Math.abs(i - 3)];
                if (progress == null) return null;
                return (
                    <GuideCard
                        key={i}
                        index={i}
                        guide={guide}
                        progress={progress}
                    />
                );
            })}
        </View>
    );
};

const SummaryStep = ({
    onBack,
    narrow = false,
}: {
    onBack: () => void;
    narrow?: boolean;
}) => {
    const { guides, renderAccountForm, cmmFinalArtifactSegment } =
        useConversationalMMContext();
    const { getColor } = useThemeColors();

    const content = (() => {
        switch (cmmFinalArtifactSegment) {
            case 'guides':
                return (
                    <>
                        <Text
                            fontSize={32}
                            lineHeight="90%"
                            letterSpacing={-1.6}
                            textAlign="center"
                            style={{
                                fontFamily: FontFamilyName.SourceSerifRegular,
                            }}
                        >
                            Your system is waiting. Let&apos;s activate it.
                        </Text>
                        <GuideCardStack guides={guides} />
                        <BodyMediumRegular color="$gray1000" textAlign="center">
                            Next up, meet your Fruitful Guide to fine-tune your
                            plan and get it ready to run.
                        </BodyMediumRegular>
                    </>
                );
            case 'system':
                return (
                    <>
                        <Text
                            fontSize={32}
                            lineHeight="90%"
                            letterSpacing={-1.6}
                            textAlign="center"
                            style={{
                                fontFamily: FontFamilyName.SourceSerifRegular,
                            }}
                        >
                            Your system is waiting. Let&apos;s activate it.
                        </Text>
                        <BodyMediumRegular color="$gray1000" textAlign="center">
                            Next up, fine-tune your money system in the app and
                            turn it on.
                        </BodyMediumRegular>
                    </>
                );
            case 'account':
            default:
                return (
                    <>
                        <Text
                            fontSize={32}
                            lineHeight="90%"
                            letterSpacing={-1.6}
                            textAlign="center"
                            style={{
                                fontFamily: FontFamilyName.SourceSerifRegular,
                            }}
                        >
                            Create your account
                        </Text>
                    </>
                );
        }
    })();

    return (
        <YStack
            flex={narrow ? 1 : undefined}
            width="100%"
            alignItems="center"
            paddingVertical={24}
            paddingBottom={narrow ? 0 : 24}
            gap={24}
        >
            {narrow && (
                <XStack
                    width="100%"
                    alignItems="center"
                    justifyContent="center"
                    position="relative"
                    minHeight={40}
                >
                    <View
                        position="absolute"
                        left={0}
                        width={40}
                        height={40}
                        borderRadius={12}
                        backgroundColor="$white"
                        alignItems="center"
                        justifyContent="center"
                        cursor="pointer"
                        onPress={onBack}
                    >
                        <Icon icon={ArrowLeft} size={20} color="$black" />
                    </View>
                    <FruitfulLogo
                        width={40}
                        height={34}
                        color={getColor('$leaf')}
                    />
                </XStack>
            )}
            {!narrow && (
                <FruitfulLogo
                    width={40}
                    height={34}
                    color={getColor('$leaf')}
                />
            )}
            <YStack
                flex={narrow ? 1 : undefined}
                alignItems="center"
                justifyContent="center"
                gap={24}
            >
                {content}
                {renderAccountForm()}
            </YStack>
        </YStack>
    );
};

const CROSSFADE_DURATION = 120;
// Opacity crossfade duration for the System-step preview ↔ body hand-off
// (triggered by the "See how it works" button, not automatically).
const SYSTEM_CROSSFADE_MS = 500;

// Narrow runs the whole explainer open/close animation faster than wide (member
// feedback: the full-speed collapse felt too slow on mobile). Both still
// animate; this just scales every delay/duration in the body's collapsibles.
const NARROW_COLLAPSE_SCALE = 0.6;

const SLIDE_IN_DISTANCE = 20;

const STAGGER_DELAY = 80;

const useCrossfadeText = (
    title: string,
    contentItems: React.ReactNode[],
    {
        slideIn = false,
        fadeInDelay = 0,
    }: { slideIn?: boolean; fadeInDelay?: number } = {},
) => {
    const textOpacity = useSharedValue(1);
    const textTranslateY = useSharedValue(0);
    const [displayedTitle, setDisplayedTitle] = useState(title);
    const [displayedItems, setDisplayedItems] = useState(contentItems);
    const [staggerKey, setStaggerKey] = useState(0);

    const pendingRef = useRef({ title, contentItems });
    pendingRef.current = { title, contentItems };

    const applyPending = useCallback(() => {
        setDisplayedTitle(pendingRef.current.title);
        setDisplayedItems(pendingRef.current.contentItems);
        setStaggerKey((k) => k + 1);
    }, []);

    useEffect(() => {
        if (title === displayedTitle) {
            setDisplayedItems(contentItems);
            return;
        }
        textOpacity.value = withTiming(
            0,
            { duration: CROSSFADE_DURATION },
            () => {
                runOnJS(applyPending)();
                if (slideIn) {
                    textTranslateY.value = SLIDE_IN_DISTANCE;
                    textTranslateY.value = withDelay(
                        fadeInDelay,
                        withTiming(0, {
                            duration: CROSSFADE_DURATION * 2,
                            easing: Easing.out(Easing.ease),
                        }),
                    );
                }
                textOpacity.value = withDelay(
                    fadeInDelay,
                    withTiming(1, {
                        duration: CROSSFADE_DURATION,
                    }),
                );
            },
        );
    }, [title, contentItems]);

    const textFadeStyle = useAnimatedStyle(() => ({
        opacity: textOpacity.value,
        transform: [{ translateY: textTranslateY.value }],
    }));

    return { displayedTitle, displayedItems, textFadeStyle, staggerKey };
};

const StaggeredChild = ({
    child,
    index,
    baseDelay,
}: {
    child: React.ReactNode;
    index: number;
    baseDelay: number;
}) => {
    const delay = baseDelay + index * STAGGER_DELAY;

    const entering = () => {
        'worklet';
        return {
            initialValues: {
                opacity: 0,
                transform: [{ translateY: SLIDE_IN_DISTANCE }],
            },
            animations: {
                opacity: withDelay(
                    delay,
                    withTiming(1, { duration: CROSSFADE_DURATION }),
                ),
                transform: [
                    {
                        translateY: withDelay(
                            delay,
                            withTiming(0, {
                                duration: CROSSFADE_DURATION * 2,
                                easing: Easing.out(Easing.ease),
                            }),
                        ),
                    },
                ],
            },
        };
    };

    return <Animated.View entering={entering}>{child}</Animated.View>;
};

const StaggeredChildren = ({
    children,
    staggerKey,
    baseDelay = 0,
}: {
    children: React.ReactNode;
    staggerKey: number;
    baseDelay?: number;
}) => {
    const items = React.Children.toArray(children);

    if (isWeb) {
        const [visible, setVisible] = useState(staggerKey === 0);

        useEffect(() => {
            if (staggerKey === 0) return undefined;
            setVisible(false);
            let inner: number;
            const outer = requestAnimationFrame(() => {
                inner = requestAnimationFrame(() => {
                    setVisible(true);
                });
            });
            return () => {
                cancelAnimationFrame(outer);
                cancelAnimationFrame(inner);
            };
        }, [staggerKey]);

        return (
            <>
                {items.map((child, i) => (
                    <View
                        key={`${staggerKey}-${i}`}
                        style={{
                            opacity: visible ? 1 : 0,
                            transform: visible
                                ? 'translateY(0px)'
                                : `translateY(${SLIDE_IN_DISTANCE}px)`,
                            transition: visible
                                ? `opacity ${CROSSFADE_DURATION}ms ease ${baseDelay + i * STAGGER_DELAY}ms, transform ${CROSSFADE_DURATION * 2}ms ease-out ${baseDelay + i * STAGGER_DELAY}ms`
                                : 'none',
                        }}
                    >
                        {child}
                    </View>
                ))}
            </>
        );
    }

    return (
        <>
            {items.map((child, i) =>
                staggerKey === 0 ? (
                    <View key={`${staggerKey}-${i}`}>{child}</View>
                ) : (
                    <StaggeredChild
                        key={`${staggerKey}-${i}`}
                        child={child}
                        index={i}
                        baseDelay={baseDelay}
                    />
                ),
            )}
        </>
    );
};

const WIDE_BREAKPOINT = 770;

// Stagger delay (ms) between each child in the Aha entrance.
const AHA_STAGGER = 200;

// Tamagui's types don't include `delay` in the animation array options yet,
// but the runtime (moti driver) supports it. Build typed constants so the
// cast is in one place and the JSX stays clean.
const lazyDelayed = (ms: number) =>
    ['lazy', { delay: ms }] as unknown as 'lazy';

const AhaMomentSlide = ({
    onNext,
    useGradientBackground = false,
    forceNarrow = false,
}: {
    onNext: () => void;
    useGradientBackground?: boolean;
    forceNarrow?: boolean;
}) => {
    const { getColor } = useThemeColors();

    const { ahaResult: result } = useConversationalMMContext();
    const {
        ruleSetDisplays,
        adjustedMoneyMap,
        adjustedAhaResult,
        wasAdjusted,
        hideDates,
    } = useGetRuleSets(true);
    const ahaResult = (adjustedAhaResult ?? result) as
        | DebtAhaResult
        | SavingsAhaResult;

    const introText = useIntroText(ahaResult);
    const showSystemPreview =
        ahaResult.type !== 'debt' || wasAdjusted || hideDates;

    const { width } = useWindowDimensions();
    const isWide = !forceNarrow && width > WIDE_BREAKPOINT;

    const { onTrackEvent } = useConversationalMMContext();

    useEffect(() => {
        onTrackEvent(
            ConversationalMMAnalyticsEvent.ResultsRevealed,
            undefined,
            {
                sendImmediately: true,
            },
        );
    }, []);

    // For the timeline graph, delay internal animations (line-draw, icons)
    // until after the graph container's enterStyle has settled.
    // Graph enter delay = AHA_STAGGER * 2 (400ms), lazy spring ≈ 500ms to settle.
    const GRAPH_ENTER_SETTLE_MS = AHA_STAGGER * 2 + 500;
    const [timelineDrawLines, setTimelineDrawLines] =
        useState(showSystemPreview);
    const [timelineHideIcons, setTimelineHideIcons] =
        useState(!showSystemPreview);

    // Total internal graph animation: BG(200) + lines(400) + labels(150) = 750ms
    const GRAPH_INTERNAL_ANIM_MS = 750;

    useEffect(() => {
        if (showSystemPreview) return undefined;
        const drawTimer = setTimeout(() => {
            setTimelineDrawLines(true);
        }, GRAPH_ENTER_SETTLE_MS);
        const iconsTimer = setTimeout(() => {
            setTimelineHideIcons(false);
        }, GRAPH_ENTER_SETTLE_MS + GRAPH_INTERNAL_ANIM_MS);
        return () => {
            clearTimeout(drawTimer);
            clearTimeout(iconsTimer);
        };
    }, [showSystemPreview]);

    // Button should wait for the graph to fully finish animating.
    const buttonDelay = showSystemPreview
        ? AHA_STAGGER * 3
        : GRAPH_ENTER_SETTLE_MS + GRAPH_INTERNAL_ANIM_MS + 200;

    const content = (
        <YStack
            gap={12}
            alignItems="center"
            maxWidth={isWide ? 450 : undefined}
            width="100%"
        >
            <FruitfulLogo
                width={24}
                height={20}
                color={getColor('$leafDark')}
            />
            <BodyMediumRegular
                textAlign="center"
                {...Animations.SlowlyFadeInFromBelow}
            >
                {showSystemPreview ? 'Your Money Map is ready!' : introText}
            </BodyMediumRegular>
            <Text
                fontSize={40}
                lineHeight={40}
                letterSpacing={-2}
                textAlign="center"
                style={{
                    fontFamily: FontFamilyName.SourceSerifRegular,
                }}
                {...Animations.SlowlyFadeInFromBelow}
                animation={lazyDelayed(AHA_STAGGER)}
            >
                {showSystemPreview
                    ? NO_DATE_HEADLINE
                    : ahaResult.displayHeadline}
                {!showSystemPreview &&
                    ahaResult.debtFreeDate !== '' &&
                    `\n${ahaResult.debtFreeDate}`}
            </Text>
            <View
                width="100%"
                marginTop={20}
                marginBottom={showSystemPreview ? -24 : 0}
                overflow="hidden"
                maxHeight={450}
                style={isWide ? { height: '50vh' } : { aspectRatio: 375 / 445 }}
                {...Animations.SlowlyFadeInFromBelow}
                animation={lazyDelayed(AHA_STAGGER * 2)}
            >
                {showSystemPreview ? (
                    <>
                        <OnboardingMoneyMapPreview
                            mode="system"
                            disableAnimations
                            incomeTotalCents={adjustedMoneyMap.incomeTotalCents}
                            billsTotalCents={adjustedMoneyMap.billsTotalCents}
                            spendTotalCents={adjustedMoneyMap.spendTotalCents}
                            goalsTotalCents={adjustedMoneyMap.goalsTotalCents}
                            ruleSetDisplays={ruleSetDisplays}
                        />
                        <View
                            pointerEvents="none"
                            position="absolute"
                            left={0}
                            right={0}
                            bottom={0}
                            height="40%"
                        >
                            {!useGradientBackground && (
                                <LinearGradient
                                    colors={[
                                        'transparent',
                                        'rgba(255,255,255,0.6)',
                                    ]}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                    }}
                                />
                            )}
                            <View
                                position="absolute"
                                top={0}
                                left={0}
                                right={0}
                                bottom={0}
                                style={{
                                    backdropFilter: 'blur(6px)',
                                    WebkitBackdropFilter: 'blur(6px)',
                                    maskImage:
                                        'linear-gradient(to bottom, transparent 0%, black 60%)',
                                    WebkitMaskImage:
                                        'linear-gradient(to bottom, transparent 0%, black 60%)',
                                }}
                            />
                            {!useGradientBackground && (
                                <LinearGradient
                                    colors={[
                                        'transparent',
                                        getColor('$gray200'),
                                    ]}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                    }}
                                />
                            )}
                        </View>
                    </>
                ) : (
                    <OnboardingMoneyMapPreview
                        mode="timeline"
                        graphOnly
                        drawLines={timelineDrawLines}
                        hideIcons={timelineHideIcons}
                        popInIcons={!showSystemPreview}
                        incomeTotalCents={adjustedMoneyMap.incomeTotalCents}
                        spendTotalCents={adjustedMoneyMap.spendTotalCents}
                        goalsTotalCents={adjustedMoneyMap.goalsTotalCents}
                        ruleSetDisplays={ruleSetDisplays}
                    />
                )}
            </View>
            {isWide && (
                <View
                    marginTop={showSystemPreview ? -12 : 12}
                    width="100%"
                    {...Animations.SlowlyFadeInFromBelow}
                    animation={lazyDelayed(buttonDelay)}
                >
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        width="full"
                        text="See your money system"
                        onPress={onNext}
                    />
                </View>
            )}
        </YStack>
    );

    if (isWide) {
        return (
            <ScrollView
                flex={1}
                showsVerticalScrollIndicator={false}
                contentContainerStyle={{
                    flexGrow: 1,
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingTop: isWeb ? 24 : 0,
                    paddingBottom: 24,
                }}
            >
                {content}
            </ScrollView>
        );
    }

    return (
        <ScrollView
            flex={1}
            contentContainerStyle={{
                flexGrow: 1,
                justifyContent: 'center',
                alignItems: 'center',
                paddingTop: isWeb ? 24 : 0,
                paddingBottom: 24,
            }}
            showsVerticalScrollIndicator={false}
        >
            {content}
            <View
                marginTop={showSystemPreview ? 0 : 24}
                width="100%"
                {...Animations.SlowlyFadeInFromBelow}
                animation={lazyDelayed(buttonDelay)}
            >
                <Button
                    variant={ButtonVariant.PRIMARY}
                    size={ButtonSize.LARGE}
                    width="full"
                    text="See your money system"
                    onPress={onNext}
                />
            </View>
        </ScrollView>
    );
};

const useMoneyMapStepContent = (
    step: MoneyMapStep,
    centered = false,
): { title: string; contentItems: React.ReactNode[] } => {
    const textAlign = centered ? ('center' as const) : undefined;
    const title = useMemo(() => {
        switch (step) {
            case MoneyMapStep.Split:
                return 'Your paycheck, with a plan';
            case MoneyMapStep.System:
                return 'Your automated money system';
            case MoneyMapStep.Timeline:
                return 'See where this system takes you';
            case MoneyMapStep.Summary:
                return 'Your system is waiting';
        }
    }, [step]);

    const contentItems: React.ReactNode[] = useMemo(() => {
        switch (step) {
            case MoneyMapStep.Split:
                return [
                    <BodyMediumRegular
                        color="$gray1000"
                        key="split-1"
                        textAlign={textAlign}
                    >
                        Every month, your take-home pay is organized around
                        today&apos;s needs and tomorrow&apos;s goals.
                    </BodyMediumRegular>,
                ];
            case MoneyMapStep.System:
                return [
                    <BodyMediumRegular
                        color="$gray1000"
                        key="system"
                        textAlign={textAlign}
                    >
                        We keep it running in the background: bills are covered,
                        spend is on track, and goals fund themselves, without
                        any stress.
                    </BodyMediumRegular>,
                ];
            case MoneyMapStep.Timeline:
                return [
                    <BodyMediumRegular
                        color="$gray1000"
                        key="timeline"
                        textAlign={textAlign}
                    >
                        Once it&apos;s activated, you&apos;re on track to make
                        real progress.
                    </BodyMediumRegular>,
                ];
            case MoneyMapStep.Summary:
                return [
                    <BodyMediumRegular
                        color="$gray1000"
                        key="summary"
                        textAlign={textAlign}
                    >
                        Let&apos;s activate it. Meet your Fruitful Guide to
                        fine-tune your plan and get it ready to run.
                    </BodyMediumRegular>,
                ];
        }
    }, [step]);

    return { title: title ?? '', contentItems };
};

const MoneyMapSlide = ({
    onBack,
    forceNarrow = false,
    onExplainerChange,
    useGradientBackground = false,
}: {
    onBack: () => void;
    forceNarrow?: boolean;
    onExplainerChange?: (active: boolean) => void;
    useGradientBackground?: boolean;
}) => {
    const [step, setStep] = useState<MoneyMapStep>(MoneyMapStep.Split);
    const [explainer, setExplainer] = useState(false);

    const { top: safeTop, bottom: safeBottom } = useSafeAreaInsets();

    const { setResults, terminalAction, onTrackEvent } =
        useConversationalMMContext();

    const [sliderSpend, setSliderSpend] = useState<number | null>(null);

    const { ruleSetDisplays, adjustedMoneyMap, wasAdjusted } = useGetRuleSets(
        true,
        sliderSpend ?? undefined,
    );

    useEffect(() => {
        if (ruleSetDisplays == null) return;
        setResults((prev) => ({
            ...prev,
            goalCompletionDates: {
                ...prev.goalCompletionDates,
                [ResultSlide.Goals]: ruleSetDisplays,
            },
        }));
    }, [ruleSetDisplays, setResults]);

    const { incomeTotalCents, billsTotalCents } = adjustedMoneyMap;

    const remainingCents = useMemo(
        () => incomeTotalCents - billsTotalCents,
        [incomeTotalCents, billsTotalCents],
    );

    const spendTotalCents = sliderSpend ?? adjustedMoneyMap.spendTotalCents;
    const goalsTotalCents =
        sliderSpend != null
            ? remainingCents - sliderSpend
            : adjustedMoneyMap.goalsTotalCents;

    const sendSliderEvent = useDebouncedCallback(
        useCallback(
            (spendCents: number) => {
                onTrackEvent(ConversationalMMAnalyticsEvent.SpendGoalsSlider, {
                    spendPercentage: spendCents / remainingCents,
                });
            },
            [spendTotalCents, onTrackEvent],
        ),
        1000,
    );

    const updateSpendGoalsValues = useCallback(
        (spendCents: number) => {
            setSliderSpend(spendCents);
            setResults((prev) => ({
                ...prev,
                moneyMap: {
                    ...prev.moneyMap,
                    spendTotalCents: spendCents,
                    goalsTotalCents: remainingCents - spendCents,
                },
            }));
            sendSliderEvent(spendCents);
        },
        [setResults, remainingCents, sendSliderEvent],
    );

    const { width: windowWidth } = useWindowDimensions();
    const isWide = !forceNarrow && windowWidth > WIDE_BREAKPOINT;

    const handleSeeHowItWorks = useCallback(() => {
        setExplainer((prev) => !prev);
    }, []);

    useEffect(() => {
        if (explainer) {
            onTrackEvent(
                ConversationalMMAnalyticsEvent.ResultsExplainer,
                undefined,
                {
                    sendImmediately: true,
                },
            );
        }
    }, [explainer]);

    // Keep the interwoven explainer mounted across the whole open/close
    // animation so its copy can animate its height OUT on open and back IN on
    // hide (instead of just popping/fading):
    //  - on open: mount collapsed, then expand the copy a frame later so the
    //    Collapsibles run their max-height entrance.
    //  - on hide: collapse the copy first, then unmount once the (staggered)
    //    contract has finished, which is when the preview fades back in.
    const [bodyMounted, setBodyMounted] = useState(false);
    const [textOpen, setTextOpen] = useState(false);
    // Drives the preview ↔ body opacity crossfade. Stays true through the whole
    // open + collapse so the (opaque) body covers the preview; only flips false
    // once the boxes have settled back together, crossfading the body out while
    // the preview fades in (both mounted) so there is never a blank frame/flash.
    const [bodyOpaque, setBodyOpaque] = useState(false);

    useEffect(() => {
        if (explainer) {
            setBodyMounted(true);
            // Body is opaque immediately (its collapsed state matches the
            // preview, so the pop-in is seamless) and covers the preview.
            setBodyOpaque(true);
            // Double rAF so the collapsed (0-height) state paints for one frame
            // before we expand. With a single frame the browser never sees the
            // start value and skips the box-slide / copy transition entirely.
            let raf2 = 0;
            const raf1 = requestAnimationFrame(() => {
                raf2 = requestAnimationFrame(() => {
                    setTextOpen(true);
                });
            });
            return () => {
                cancelAnimationFrame(raf1);
                cancelAnimationFrame(raf2);
            };
        }
        // Close: collapse the copy first so the boxes animate, then — once the
        // boxes have settled back together — crossfade the body out while the
        // preview fades in (both mounted), and only unmount after that crossfade
        // so there is never a gap. Narrow runs the same animation faster.
        setTextOpen(false);
        const collapseMs =
            EXPLAINER_COLLAPSE_BASE_MS * (isWide ? 1 : NARROW_COLLAPSE_SCALE);
        const crossfadeTimer = setTimeout(() => {
            setBodyOpaque(false);
        }, collapseMs);
        const unmountTimer = setTimeout(() => {
            setBodyMounted(false);
        }, collapseMs + SYSTEM_CROSSFADE_MS);
        return () => {
            clearTimeout(crossfadeTimer);
            clearTimeout(unmountTimer);
        };
    }, [explainer, isWide]);

    // Reset the explainer whenever we leave the System step so it never lingers
    // when navigating between pages (no need to animate the collapse here).
    useEffect(() => {
        if (step !== MoneyMapStep.System) {
            setExplainer(false);
            setTextOpen(false);
            setBodyMounted(false);
            setBodyOpaque(false);
        }
    }, [step]);

    // `showComposed` stays true through the collapse (driven by `bodyMounted`,
    // not the raw `explainer` intent) so the map/chrome only reverts once the
    // copy has finished animating back in.
    const showComposed = step === MoneyMapStep.System && bodyMounted;

    useEffect(() => {
        onExplainerChange?.(showComposed);
    }, [showComposed, onExplainerChange]);

    // The button label follows the raw intent so it flips immediately on click.
    const explainerActive = step === MoneyMapStep.System && explainer;

    const { title, contentItems } = useMoneyMapStepContent(step, !isWide);

    // Delay showing the "See how it works" button so the System step
    // morph animation finishes before the CTA appears.
    const [showSeeHowButton, setShowSeeHowButton] = useState(false);
    const [showAdjustedBox, setShowAdjustedBox] = useState(
        step === MoneyMapStep.Split,
    );
    const [wideButtonSpace, setWideButtonSpace] = useState(false);
    useEffect(() => {
        if (step === MoneyMapStep.System) {
            setWideButtonSpace(true);
            const timer = setTimeout(() => {
                setShowSeeHowButton(true);
            }, 800);
            setShowAdjustedBox(false);
            return () => {
                clearTimeout(timer);
            };
        }
        setShowSeeHowButton(false);
        setWideButtonSpace(false);
        if (step === MoneyMapStep.Split) {
            const timer = setTimeout(() => {
                setShowAdjustedBox(true);
            }, SYSTEM_CROSSFADE_MS);
            return () => {
                clearTimeout(timer);
            };
        }
        setShowAdjustedBox(false);
        return undefined;
    }, [step]);

    const seeHowItWorksButton =
        step === MoneyMapStep.System ? (
            <Button
                width="auto"
                // Always PRIMARY so the "Got it" state matches the
                // "See how it works" button. (SECONDARY's $gray200 fill is the
                // same color as the left column, so it rendered as bare text.)
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.SMALL}
                text={explainerActive ? 'Got it' : 'See how it works'}
                icon={explainerActive ? undefined : ArrowRight}
                iconPosition="right"
                onPress={handleSeeHowItWorks}
                // The default hover applies a ring shadow, but this button sits
                // inside the `overflow: 'hidden'` button-area container (needed
                // for the max-height collapse), which clips that shadow and
                // makes it look broken. Use a subtle opacity hover instead.
                hoverStyle={{ opacity: 0.9 }}
            />
        ) : null;

    // Keep last button in a ref so it stays in the DOM during collapse
    // (maxHeight 80→0 needs content to maintain intrinsic height)
    const seeHowButtonRef = useRef<React.ReactNode>(null);
    if (seeHowItWorksButton) {
        seeHowButtonRef.current = seeHowItWorksButton;
    }
    const renderedButton = seeHowItWorksButton ?? seeHowButtonRef.current;
    const {
        displayedTitle: wideTitle,
        displayedItems: wideItems,
        textFadeStyle: wideFadeStyle,
        staggerKey: wideStaggerKey,
    } = useCrossfadeText(title, contentItems, {
        slideIn: true,
        fadeInDelay: CROSSFADE_DURATION * 2,
    });

    const estimatedTextTimer = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );
    const showEstimatedTextAfterDelay = useCallback((nextStep: number) => {
        if (estimatedTextTimer.current != null) {
            clearTimeout(estimatedTextTimer.current);
            estimatedTextTimer.current = null;
        }
        const shouldShow =
            nextStep >= MoneyMapStep.System &&
            nextStep <= MoneyMapStep.Timeline;
        if (!shouldShow) {
            setShowEstimatedText(false);
        } else {
            estimatedTextTimer.current = setTimeout(() => {
                setShowEstimatedText(true);
            }, CROSSFADE_DURATION * 6);
        }
    }, []);

    const lastStep =
        terminalAction.mode === 'exit'
            ? MoneyMapStep.Timeline
            : MoneyMapStep.Summary;

    const isAtTerminal =
        terminalAction.mode === 'exit' && step === MoneyMapStep.Timeline;

    const nextButtonText =
        isAtTerminal && terminalAction.mode === 'exit'
            ? (terminalAction.label ?? 'Back to homepage')
            : 'Next';
    const showNextArrow = !isAtTerminal;
    const terminalLoading =
        terminalAction.mode === 'signup' && terminalAction.isLoading === true;

    // --- Wide-layout state ---
    const hasSteppedOnce = useRef(false);
    // Height of the left content column, used to size the money-map preview.
    const [wideColumnHeight, setWideColumnHeight] = useState(0);
    const [wideTextMinHeight, setWideTextMinHeight] = useState(0);
    const onWideTextLayout = useCallback((e: LayoutChangeEvent) => {
        const h = e.nativeEvent.layout.height;
        setWideTextMinHeight((prev) => Math.max(prev, h));
    }, []);
    const [wideGraphMinHeight, setWideGraphMinHeight] = useState(0);
    const onWideGraphLayout = useCallback((e: LayoutChangeEvent) => {
        const h = e.nativeEvent.layout.height;
        setWideGraphMinHeight((prev) => Math.max(prev, h));
    }, []);

    // --- Narrow-layout state (top header + bottom segmented nav) ---
    const [buttonBarHeight, setButtonBarHeight] = useState(0);
    const [narrowScrollOffset, setNarrowScrollOffset] = useState(0);
    const scrollRef = useRef<RNScrollView>(null);
    const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);
    const narrowScrollY = useSharedValue(0);
    const onNarrowScroll = useCallback(
        (e: { nativeEvent: { contentOffset: { y: number } } }) => {
            narrowScrollY.value = e.nativeEvent.contentOffset.y;
            setNarrowScrollOffset(e.nativeEvent.contentOffset.y);
        },
        [narrowScrollY],
    );
    const lineItemsAnimStyle = useAnimatedStyle(() => {
        const progress = interpolate(
            narrowScrollY.value,
            [0, 80],
            [0, 1],
            'clamp',
        );
        return {
            opacity: 1 - progress,
            transform: [{ translateY: -progress * 6 }],
        };
    });
    const [showEstimatedText, setShowEstimatedText] = useState(
        step >= MoneyMapStep.System && step <= MoneyMapStep.Timeline,
    );
    const [, setBoxPositions] =
        useState<OnboardingMoneyMapSystemBoxPositions | null>(null);
    const [savedGoalIcons, setSavedGoalIcons] = useState<GoalIconPosition[]>(
        [],
    );
    const scrollViewHeightRef = useRef(0);

    const prevStepRef = useRef(step);
    const animateStep = useCallback(
        (nextStep: number) => {
            const prev = prevStepRef.current;
            prevStepRef.current = nextStep;
            setStep(nextStep);
            showEstimatedTextAfterDelay(nextStep);
            const isSystemTimelineSwap =
                (prev === MoneyMapStep.System &&
                    nextStep === MoneyMapStep.Timeline) ||
                (prev === MoneyMapStep.Timeline &&
                    nextStep === MoneyMapStep.System);
            if (isSystemTimelineSwap) {
                setTimeout(() => {
                    scrollRef.current?.scrollTo({
                        y: 0,
                        animated: false,
                    });
                }, SYSTEM_CROSSFADE_MS);
            } else {
                scrollRef.current?.scrollTo({ y: 0, animated: true });
            }
        },
        [showEstimatedTextAfterDelay],
    );

    const handlePrev = useCallback(() => {
        hasSteppedOnce.current = true;
        onTrackEvent(ConversationalMMAnalyticsEvent.ResultsPrev, undefined, {
            sendImmediately: true,
        });
        if (step > MoneyMapStep.Split) {
            animateStep(step - 1);
        } else {
            onBack();
        }
    }, [step, onBack, animateStep]);

    const handleNext = useCallback(() => {
        hasSteppedOnce.current = true;

        if (isAtTerminal) {
            onTrackEvent(
                ConversationalMMAnalyticsEvent.ResultsNext,
                undefined,
                { sendImmediately: true },
            );
            terminalAction.onPress();
        } else if (step < lastStep) {
            onTrackEvent(
                ConversationalMMAnalyticsEvent.ResultsNext,
                undefined,
                { sendImmediately: true },
            );
            const next = step + 1;
            animateStep(next);
        }
    }, [step, lastStep, terminalAction, animateStep, isAtTerminal]);

    useEffect(() => {
        if (step === MoneyMapStep.Split) {
            scrollRef.current?.scrollTo({ y: 0, animated: false });
        }
    }, [step]);

    useEffect(() => {
        if (showComposed) {
            scrollRef.current?.scrollTo({ y: 0, animated: false });
        }
    }, [showComposed]);

    const nativeHeaderTop = Math.max(safeTop, 16);
    const scrollPaddingTop = isWeb ? 24 : 8;
    const scrollPaddingBottom = buttonBarHeight;
    const [narrowScrollHeight, setNarrowScrollHeight] = useState(0);
    // Viewport height of the wide scroll area, used to pin the right column's
    // content to the viewport center so it doesn't drift when the left column
    // grows with the expanded explainer.
    const [wideViewportHeight, setWideViewportHeight] = useState(0);
    const [adjustedBoxHeight, setAdjustedBoxHeight] = useState(0);
    // On native, use a safe fallback for the bottom bar height before it
    // measures, so the graph doesn't overflow into the nav on first render.
    // Add a small buffer (16px) for content that sits between the graph and
    // the bottom bar (e.g. button container padding, layout rounding).
    const effectiveButtonBarHeight = isWeb
        ? buttonBarHeight
        : Math.max(buttonBarHeight, Math.max(safeBottom, 12) + 68) + 16;
    const narrowAvailableHeight =
        step === MoneyMapStep.Split
            ? narrowScrollHeight -
              stickyHeaderHeight -
              effectiveButtonBarHeight -
              adjustedBoxHeight
            : narrowScrollHeight - stickyHeaderHeight - scrollPaddingBottom;

    const previewMode = useMemo(() => {
        switch (step) {
            case MoneyMapStep.Split:
                return 'split';
            case MoneyMapStep.Timeline:
            case MoneyMapStep.Summary:
                return 'timeline';
            default:
                return 'system';
        }
    }, [step]);

    const highlightedBox: OnboardingMoneyMapHighlightedBox = null;

    // The Summary page replaces the map/copy with a guide hand-off screen.
    const isSummary = step === MoneyMapStep.Summary;
    const hiddenStyle = isSummary
        ? ({
              opacity: 0,
              position: 'absolute',
              pointerEvents: 'none',
              width: '100%',
          } as const)
        : undefined;

    const handleScrollLayout = useCallback(
        (e: LayoutChangeEvent) => {
            const h = e.nativeEvent.layout.height;
            if (isWide) {
                setWideViewportHeight(h);
                return;
            }
            scrollViewHeightRef.current = h;
            setNarrowScrollHeight(h);
        },
        [isWide],
    );

    useEffect(() => {
        onTrackEvent(ConversationalMMResultsScreenEvent[step], undefined, {
            sendImmediately: true,
        });
    }, [step]);

    return (
        <>
            <ScrollView
                ref={scrollRef}
                flex={1}
                width="100%"
                zIndex={2}
                showsVerticalScrollIndicator={isWide ? false : !isWeb}
                scrollEnabled={isWide || step !== MoneyMapStep.Split}
                onScroll={isWide ? undefined : onNarrowScroll}
                scrollEventThrottle={16}
                contentContainerStyle={
                    isWide
                        ? {
                              flexGrow: 1,
                          }
                        : {
                              ...(isSummary && { flexGrow: 1 }),
                              paddingBottom: showComposed
                                  ? 100
                                  : effectiveButtonBarHeight,
                          }
                }
                onLayout={handleScrollLayout}
            >
                {isWide ? (
                    <>
                        <XStack flex={1} width="100%" style={hiddenStyle}>
                            {/* LEFT: gray column with the floating content card */}
                            <YStack
                                flex={1}
                                flexBasis="0%"
                                backgroundColor="$gray200"
                                alignItems="center"
                                justifyContent="flex-start"
                                paddingHorizontal={48}
                                paddingVertical={48}
                                onLayout={(e: LayoutChangeEvent) => {
                                    setWideColumnHeight(
                                        e.nativeEvent.layout.height,
                                    );
                                }}
                            >
                                <YStack
                                    flex={1}
                                    width="100%"
                                    alignItems="center"
                                    justifyContent="flex-start"
                                >
                                    {/* Auto margins center the content while it
                                    fits, and collapse to top-aligned (scrollable,
                                    no clipping) once the explainer outgrows the
                                    viewport — this avoids the bounce that a
                                    justifyContent center↔flex-start swap caused
                                    when the body mounted/unmounted. */}
                                    <YStack
                                        width="100%"
                                        alignItems="center"
                                        style={{
                                            marginTop: 'auto',
                                            marginBottom: 'auto',
                                        }}
                                    >
                                        {/* "See how it works" / "Hide" toggle
                                    24px above the system preview.
                                    Always mounted; animated via max-height
                                    so the preview slides smoothly. */}
                                        <View
                                            style={{
                                                overflow: 'hidden',
                                                maxHeight: wideButtonSpace
                                                    ? 56
                                                    : 0,
                                                opacity: showSeeHowButton
                                                    ? 1
                                                    : 0,
                                                transition:
                                                    'max-height 350ms cubic-bezier(0.25,0.1,0.25,1), opacity 300ms ease',
                                            }}
                                            pointerEvents={
                                                showSeeHowButton
                                                    ? 'auto'
                                                    : 'none'
                                            }
                                        >
                                            <XStack
                                                width="100%"
                                                maxWidth={440}
                                                alignSelf="center"
                                                justifyContent="center"
                                                marginBottom={12}
                                            >
                                                {renderedButton}
                                            </XStack>
                                        </View>
                                        {/* Title slot: reserved whenever we're
                                        on the System step (so its height
                                        appears/collapses only on step change,
                                        like the button) and merely fades its
                                        opacity when the explainer opens/closes.
                                        This keeps the map anchored so it never
                                        jumps down just for the title. */}
                                        <View
                                            style={{
                                                overflow: 'hidden',
                                                maxHeight:
                                                    step === MoneyMapStep.System
                                                        ? 56
                                                        : 0,
                                                opacity: textOpen ? 1 : 0,
                                                transition:
                                                    'max-height 350ms cubic-bezier(0.25,0.1,0.25,1), opacity 300ms ease',
                                            }}
                                            pointerEvents="none"
                                        >
                                            <YStack
                                                width="100%"
                                                maxWidth={440}
                                                alignSelf="center"
                                            >
                                                <H5Emphasized marginBottom={24}>
                                                    How your system works
                                                </H5Emphasized>
                                            </YStack>
                                        </View>
                                        <YStack
                                            width="100%"
                                            maxWidth={440}
                                            alignSelf="center"
                                            position="relative"
                                            onLayout={onWideGraphLayout}
                                            minHeight={
                                                step === MoneyMapStep.Timeline
                                                    ? wideGraphMinHeight ||
                                                      undefined
                                                    : undefined
                                            }
                                        >
                                            {/* Preview drives the morphs across all steps.
                                        On System it fades out once the interwoven
                                        explainer crossfades in, but stays mounted so
                                        it can morph back when leaving the explainer. */}
                                            {wideColumnHeight > 0 && (
                                                <View
                                                    pointerEvents={
                                                        showComposed
                                                            ? 'none'
                                                            : 'auto'
                                                    }
                                                    style={{
                                                        opacity: bodyOpaque
                                                            ? 0
                                                            : 1,
                                                        transition: `opacity ${SYSTEM_CROSSFADE_MS}ms ease`,
                                                        ...(showComposed
                                                            ? {
                                                                  position:
                                                                      'absolute',
                                                                  top: 0,
                                                                  left: 0,
                                                                  right: 0,
                                                              }
                                                            : {}),
                                                    }}
                                                >
                                                    <OnboardingMoneyMapPreview
                                                        incomeTotalCents={
                                                            incomeTotalCents
                                                        }
                                                        billsTotalCents={
                                                            billsTotalCents
                                                        }
                                                        spendTotalCents={
                                                            spendTotalCents
                                                        }
                                                        goalsTotalCents={
                                                            goalsTotalCents
                                                        }
                                                        mode={previewMode}
                                                        maxBarHeight={
                                                            wasAdjusted
                                                                ? 160
                                                                : 200
                                                        }
                                                        ruleSetDisplays={
                                                            ruleSetDisplays
                                                        }
                                                        highlightedBox={
                                                            highlightedBox
                                                        }
                                                        onSpendSlideMove={
                                                            updateSpendGoalsValues
                                                        }
                                                        onSpendSlideEnd={
                                                            updateSpendGoalsValues
                                                        }
                                                        onSystemGoalIcons={
                                                            setSavedGoalIcons
                                                        }
                                                        savedSystemGoalIcons={
                                                            savedGoalIcons
                                                        }
                                                    />
                                                    <View
                                                        style={{
                                                            overflow: 'hidden',
                                                            maxHeight:
                                                                showEstimatedText
                                                                    ? 40
                                                                    : 0,
                                                            opacity:
                                                                showEstimatedText
                                                                    ? 1
                                                                    : 0,
                                                            transition:
                                                                'max-height 350ms cubic-bezier(0.25,0.1,0.25,1), opacity 300ms ease',
                                                        }}
                                                    >
                                                        <LabelRegular
                                                            color="$gray1000"
                                                            textAlign="center"
                                                            marginTop={8}
                                                        >
                                                            All dates are
                                                            estimates
                                                        </LabelRegular>
                                                    </View>
                                                </View>
                                            )}
                                            {/* Absolute so it doesn't affect vertical centering */}
                                            {step === MoneyMapStep.Split &&
                                                wasAdjusted && (
                                                    <View
                                                        position="absolute"
                                                        left={0}
                                                        right={0}
                                                        style={{
                                                            top: '100%',
                                                            opacity:
                                                                showAdjustedBox
                                                                    ? 1
                                                                    : 0,
                                                            transition:
                                                                'opacity 300ms ease',
                                                        }}
                                                        pointerEvents={
                                                            showAdjustedBox
                                                                ? 'auto'
                                                                : 'none'
                                                        }
                                                        marginTop={16}
                                                        padding={16}
                                                        borderRadius={24}
                                                        backgroundColor="$peachLight"
                                                    >
                                                        <BodySmallRegular
                                                            fontStyle="italic"
                                                            color="$peachDarkest"
                                                        >
                                                            Based on what you
                                                            shared, there
                                                            isn&apos;t enough
                                                            left for goals and
                                                            spending.
                                                            That&apos;s okay;
                                                            Fruitful can help
                                                            rebalance things and
                                                            build a system like
                                                            this.
                                                        </BodySmallRegular>
                                                    </View>
                                                )}
                                        </YStack>
                                        {/* On System, the interwoven explainer
                                    crossfades in below (over) the preview once
                                    the user clicks "See how it works". Kept out
                                    of the measured graph container so it doesn't
                                    inflate the Timeline min-height. */}
                                        {step === MoneyMapStep.System &&
                                            showComposed && (
                                                <YStack
                                                    width="100%"
                                                    maxWidth={440}
                                                    alignSelf="center"
                                                    // Body stays fully opaque
                                                    // the whole time it's
                                                    // mounted. Its boxes are
                                                    // solid, so cross-dissolving
                                                    // it with the (identical,
                                                    // aligned) preview would dip
                                                    // both to ~50% and flash the
                                                    // grey through. Instead the
                                                    // preview fades in hidden
                                                    // behind it, then we unmount
                                                    // the body to reveal it.
                                                    style={{ opacity: 1 }}
                                                >
                                                    <HowSystemWorksBody
                                                        incomeTotalCents={
                                                            incomeTotalCents
                                                        }
                                                        billsTotalCents={
                                                            billsTotalCents
                                                        }
                                                        spendTotalCents={
                                                            spendTotalCents
                                                        }
                                                        goalsTotalCents={
                                                            goalsTotalCents
                                                        }
                                                        ruleSetDisplays={
                                                            ruleSetDisplays ??
                                                            null
                                                        }
                                                        showText={textOpen}
                                                    />
                                                </YStack>
                                            )}
                                    </YStack>
                                </YStack>
                            </YStack>
                            {/* RIGHT: white column with tab switcher, copy + CTA.
                            The white background fills the full (possibly tall)
                            row, but the content is pinned to the viewport center
                            via a sticky box so it never moves when the left
                            column grows with the expanded explainer. */}
                            <YStack
                                flex={1}
                                flexBasis="0%"
                                backgroundColor="$white"
                                justifyContent="flex-start"
                                paddingLeft={80}
                                paddingRight={48}
                            >
                                <YStack
                                    justifyContent="center"
                                    style={{
                                        position: 'sticky' as 'relative',
                                        top: 0,
                                        // Fall back to the viewport height until
                                        // the ScrollView reports its measured
                                        // height. Without this, the first paint
                                        // has no height, so the content pins to
                                        // the top and then visibly jumps to
                                        // center once measurement lands.
                                        height: wideViewportHeight || '100vh',
                                    }}
                                >
                                    <YStack width={320} gap={40}>
                                        {/* Tab switcher (reflects the active page) */}
                                        <XStack
                                            alignSelf="flex-start"
                                            backgroundColor="$gray200"
                                            borderRadius={12}
                                            padding={2}
                                            gap={2}
                                        >
                                            {MONEY_MAP_TABS.map((tab) => (
                                                <View
                                                    key={tab.step}
                                                    width={36}
                                                    height={36}
                                                    borderRadius={9}
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    backgroundColor={
                                                        tab.step === step
                                                            ? tab.color
                                                            : 'transparent'
                                                    }
                                                    cursor="pointer"
                                                    onPress={() => {
                                                        if (tab.step === step)
                                                            return;
                                                        hasSteppedOnce.current = true;
                                                        setStep(tab.step);
                                                        showEstimatedTextAfterDelay(
                                                            tab.step,
                                                        );
                                                    }}
                                                >
                                                    <Icon
                                                        icon={tab.icon}
                                                        size={22}
                                                        color="$black"
                                                    />
                                                </View>
                                            ))}
                                        </XStack>
                                        <View
                                            onLayout={onWideTextLayout}
                                            style={{
                                                minHeight:
                                                    wideTextMinHeight ||
                                                    undefined,
                                            }}
                                        >
                                            <Animated.View
                                                style={wideFadeStyle}
                                            >
                                                <YStack gap={12}>
                                                    <BodyLargeEmphasized>
                                                        {wideTitle}
                                                    </BodyLargeEmphasized>
                                                    <YStack gap={16}>
                                                        <StaggeredChildren
                                                            staggerKey={
                                                                wideStaggerKey
                                                            }
                                                            baseDelay={
                                                                CROSSFADE_DURATION *
                                                                2
                                                            }
                                                        >
                                                            {wideItems}
                                                        </StaggeredChildren>
                                                    </YStack>
                                                </YStack>
                                            </Animated.View>
                                        </View>
                                        <XStack>
                                            <Button
                                                width="auto"
                                                variant={ButtonVariant.PRIMARY}
                                                size={ButtonSize.LARGE}
                                                text={nextButtonText}
                                                icon={
                                                    terminalLoading ? (
                                                        <Spinner
                                                            size={
                                                                SpinnerSize.SMALL
                                                            }
                                                            width="auto"
                                                        />
                                                    ) : showNextArrow ? (
                                                        ArrowRight
                                                    ) : undefined
                                                }
                                                iconPosition="right"
                                                disabled={terminalLoading}
                                                onPress={handleNext}
                                            />
                                        </XStack>
                                    </YStack>
                                </YStack>
                            </YStack>
                            {/* Back button pinned to the top-left corner */}
                            <View
                                position="absolute"
                                top={36}
                                left={36}
                                zIndex={5}
                            >
                                <IconButton
                                    variant={ButtonVariant.SECONDARY_ALT}
                                    icon={ArrowLeft}
                                    onPress={handlePrev}
                                />
                            </View>
                        </XStack>
                        {isSummary && (
                            <>
                                <YStack
                                    width="100%"
                                    maxWidth={450}
                                    alignSelf="center"
                                    flex={1}
                                    justifyContent="center"
                                >
                                    <SummaryStep onBack={handlePrev} />
                                </YStack>
                                <View
                                    position="absolute"
                                    top={36}
                                    left={36}
                                    zIndex={5}
                                >
                                    <IconButton
                                        variant={ButtonVariant.SECONDARY_ALT}
                                        icon={ArrowLeft}
                                        onPress={handlePrev}
                                    />
                                </View>
                            </>
                        )}
                    </>
                ) : (
                    <>
                        {/* Spacer matching sticky header height (collapse in explainer) */}
                        {!isSummary && (
                            <View
                                style={{
                                    height: showComposed
                                        ? 20
                                        : stickyHeaderHeight,
                                    ...(isWeb
                                        ? {
                                              transition: 'height 300ms ease',
                                          }
                                        : {}),
                                }}
                            />
                        )}
                        <View style={hiddenStyle}>
                            {showComposed && (
                                <H5Emphasized marginBottom={16}>
                                    How your system works
                                </H5Emphasized>
                            )}
                            {!showComposed && (
                                <XStack
                                    width="100%"
                                    paddingBottom={16}
                                    justifyContent="center"
                                    zIndex={5}
                                    style={{
                                        maxHeight: seeHowItWorksButton ? 80 : 0,
                                        overflow: seeHowItWorksButton
                                            ? ('visible' as 'hidden')
                                            : 'hidden',
                                        ...(isWeb && seeHowItWorksButton
                                            ? {
                                                  position:
                                                      'sticky' as 'relative',
                                                  top:
                                                      scrollPaddingTop + 40 + 8,
                                                  ...(!useGradientBackground
                                                      ? {
                                                            backdropFilter:
                                                                'blur(16px)',
                                                            WebkitBackdropFilter:
                                                                'blur(16px)',
                                                            backgroundColor:
                                                                'rgba(245, 245, 245, 0.75)',
                                                        }
                                                      : {}),
                                              }
                                            : {}),
                                        opacity:
                                            seeHowItWorksButton &&
                                            showSeeHowButton
                                                ? 1
                                                : 0,
                                        ...(isWeb
                                            ? {
                                                  transition:
                                                      'max-height 350ms cubic-bezier(0.25,0.1,0.25,1), opacity 300ms ease',
                                              }
                                            : {}),
                                    }}
                                    pointerEvents={
                                        showSeeHowButton ? 'auto' : 'none'
                                    }
                                >
                                    {renderedButton}
                                </XStack>
                            )}
                            <View
                                width="100%"
                                position="relative"
                                zIndex={2}
                                backgroundColor={
                                    useGradientBackground
                                        ? 'transparent'
                                        : showComposed
                                          ? '$white'
                                          : '$gray200'
                                }
                            >
                                {/* Persistent preview drives the morphs; fades out once
                            the System crossfade lands but stays mounted so it
                            can morph back out when leaving System. */}
                                {narrowScrollHeight > 0 && (
                                    <View
                                        pointerEvents={
                                            showComposed ? 'none' : 'auto'
                                        }
                                        style={{
                                            opacity: showComposed ? 0 : 1,
                                            ...(showComposed
                                                ? {
                                                      position: 'absolute',
                                                      top: 0,
                                                      left: 0,
                                                      right: 0,
                                                  }
                                                : {}),
                                        }}
                                    >
                                        <OnboardingMoneyMapPreview
                                            incomeTotalCents={incomeTotalCents}
                                            billsTotalCents={billsTotalCents}
                                            spendTotalCents={spendTotalCents}
                                            goalsTotalCents={goalsTotalCents}
                                            mode={previewMode}
                                            ruleSetDisplays={ruleSetDisplays}
                                            availableHeight={
                                                narrowAvailableHeight
                                            }
                                            highlightedBox={highlightedBox}
                                            onSystemBoxPositions={
                                                setBoxPositions
                                            }
                                            onSpendSlideMove={
                                                updateSpendGoalsValues
                                            }
                                            onSpendSlideEnd={
                                                updateSpendGoalsValues
                                            }
                                            onSystemGoalIcons={
                                                setSavedGoalIcons
                                            }
                                            savedSystemGoalIcons={
                                                savedGoalIcons
                                            }
                                        />
                                        {showEstimatedText && (
                                            <LabelRegular
                                                color="$gray1000"
                                                textAlign="center"
                                                marginTop={8}
                                            >
                                                All dates are estimated
                                            </LabelRegular>
                                        )}
                                    </View>
                                )}
                                {step === MoneyMapStep.Split && wasAdjusted && (
                                    <View
                                        padding={16}
                                        borderRadius={24}
                                        marginTop={16}
                                        marginBottom={16}
                                        backgroundColor="$peachLight"
                                        style={{
                                            opacity: showAdjustedBox ? 1 : 0,
                                            transition: 'opacity 300ms ease',
                                        }}
                                        pointerEvents={
                                            showAdjustedBox ? 'auto' : 'none'
                                        }
                                        onLayout={(e: LayoutChangeEvent) => {
                                            setAdjustedBoxHeight(
                                                e.nativeEvent.layout.height +
                                                    16,
                                            );
                                        }}
                                    >
                                        <BodySmallRegular
                                            fontStyle="italic"
                                            color="$peachDarkest"
                                        >
                                            Based on what you shared, there
                                            isn&apos;t enough left for goals and
                                            spending. That&apos;s okay; Fruitful
                                            can help rebalance things and build
                                            a system like this.
                                        </BodySmallRegular>
                                    </View>
                                )}
                                {/* On System, the interwoven body crossfades in over
                            the preview once it has morphed in. */}
                                {step === MoneyMapStep.System &&
                                    showComposed && (
                                        <View>
                                            <HowSystemWorksBody
                                                incomeTotalCents={
                                                    incomeTotalCents
                                                }
                                                billsTotalCents={
                                                    billsTotalCents
                                                }
                                                spendTotalCents={
                                                    spendTotalCents
                                                }
                                                goalsTotalCents={
                                                    goalsTotalCents
                                                }
                                                ruleSetDisplays={
                                                    ruleSetDisplays ?? null
                                                }
                                                showText={textOpen}
                                                speedScale={
                                                    NARROW_COLLAPSE_SCALE
                                                }
                                            />
                                        </View>
                                    )}
                            </View>
                        </View>
                        {isSummary && (
                            <YStack
                                width="100%"
                                minHeight={narrowScrollHeight}
                                justifyContent="center"
                            >
                                <SummaryStep narrow onBack={handlePrev} />
                            </YStack>
                        )}
                    </>
                )}
            </ScrollView>
            {/* Line items layer — sticky behind scroll content (zIndex 1) */}
            {!isWide && !isSummary && (
                <YStack
                    position="absolute"
                    top={isWeb ? 0 : nativeHeaderTop}
                    left={0}
                    right={0}
                    paddingHorizontal={16}
                    paddingTop={scrollPaddingTop}
                    paddingBottom={16}
                    zIndex={1}
                    onLayout={(e: LayoutChangeEvent) => {
                        setStickyHeaderHeight(e.nativeEvent.layout.height);
                    }}
                    style={{
                        ...(showComposed
                            ? {
                                  opacity: 0,
                                  pointerEvents: 'none' as const,
                              }
                            : {}),
                    }}
                >
                    {/* Invisible spacer matching the title row height */}
                    <XStack
                        width="100%"
                        alignItems="center"
                        marginBottom={4}
                        opacity={0}
                        pointerEvents="none"
                    >
                        <View width={40} height={40} />
                        <View flex={1}>
                            <BodyLargeRegular textAlign="center">
                                {'\u00A0'}
                            </BodyLargeRegular>
                        </View>
                        <View width={40} />
                    </XStack>
                    <Animated.View style={[wideFadeStyle, lineItemsAnimStyle]}>
                        <YStack
                            gap={12}
                            alignItems="center"
                            width="100%"
                            style={{ textAlign: 'center' }}
                        >
                            <StaggeredChildren
                                staggerKey={wideStaggerKey}
                                baseDelay={CROSSFADE_DURATION * 2}
                            >
                                {wideItems}
                            </StaggeredChildren>
                        </YStack>
                    </Animated.View>
                </YStack>
            )}
            {/* Title row layer — sticky on top of everything (zIndex 4) */}
            {!isWide && !isSummary && (
                <XStack
                    position="absolute"
                    top={isWeb ? 0 : nativeHeaderTop}
                    left={0}
                    right={0}
                    paddingHorizontal={16}
                    paddingTop={scrollPaddingTop}
                    paddingBottom={8}
                    backgroundColor={
                        isWeb && !useGradientBackground
                            ? 'rgba(245, 245, 245, 0.75)'
                            : 'transparent'
                    }
                    style={{
                        ...(isWeb && !useGradientBackground
                            ? {
                                  backdropFilter: 'blur(16px)',
                                  WebkitBackdropFilter: 'blur(16px)',
                              }
                            : {}),
                        ...(showComposed
                            ? {
                                  opacity: 0,
                                  pointerEvents: 'none' as const,
                              }
                            : {}),
                    }}
                    zIndex={4}
                    alignItems="center"
                >
                    <View
                        width={40}
                        height={40}
                        borderRadius={12}
                        backgroundColor={
                            useGradientBackground ? 'transparent' : '$white'
                        }
                        alignItems="center"
                        justifyContent="center"
                        {...(isWeb ? { cursor: 'pointer' } : {})}
                        onPress={handlePrev}
                    >
                        <Icon icon={ArrowLeft} size={20} color="$black" />
                    </View>
                    <Animated.View style={[wideFadeStyle, { flex: 1 }]}>
                        <BodyLargeRegular textAlign="center">
                            {wideTitle}
                        </BodyLargeRegular>
                    </Animated.View>
                    <View width={40} />
                </XStack>
            )}
            {/* Top frosted blur when scrolled in explainer view (web only) */}
            {isWeb &&
                !isWide &&
                !isSummary &&
                showComposed &&
                narrowScrollOffset > 0 && (
                    <View
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        height={20}
                        zIndex={5}
                        pointerEvents="none"
                        style={{
                            backdropFilter: 'blur(16px)',
                            WebkitBackdropFilter: 'blur(16px)',
                            backgroundColor: 'rgba(255,255,255,0.3)',
                            maskImage:
                                'linear-gradient(to top, transparent 0%, black 40%)',
                            WebkitMaskImage:
                                'linear-gradient(to top, transparent 0%, black 40%)',
                        }}
                    />
                )}
            {/* Floating "Got it" CTA while the explainer is open on narrow */}
            {!isWide && !isSummary && showComposed && (
                <View
                    position="absolute"
                    bottom={0}
                    left={0}
                    right={0}
                    paddingHorizontal={16}
                    paddingBottom={Math.max(safeBottom, 12)}
                    paddingTop={48}
                    zIndex={5}
                    style={
                        isWeb
                            ? {
                                  backdropFilter: 'blur(16px)',
                                  WebkitBackdropFilter: 'blur(16px)',
                                  backgroundColor: 'rgba(255,255,255,0.3)',
                                  maskImage:
                                      'linear-gradient(to bottom, transparent 0%, black 40%)',
                                  WebkitMaskImage:
                                      'linear-gradient(to bottom, transparent 0%, black 40%)',
                              }
                            : {}
                    }
                >
                    <Button
                        width="full"
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Got it"
                        onPress={handleSeeHowItWorks}
                    />
                </View>
            )}
            {!isWide && !isSummary && !showComposed && (
                <XStack
                    position="absolute"
                    bottom={0}
                    left={0}
                    right={0}
                    paddingHorizontal={16}
                    paddingTop={8}
                    paddingBottom={Math.max(safeBottom, 12)}
                    justifyContent="space-between"
                    alignItems="center"
                    zIndex={3}
                    onLayout={(e: LayoutChangeEvent) => {
                        setButtonBarHeight(e.nativeEvent.layout.height);
                    }}
                >
                    {/* Segmented page nav (Split / System / Timeline). */}
                    <XStack
                        backgroundColor="$white"
                        borderRadius={16}
                        padding={2}
                        gap={2}
                        height={52}
                    >
                        {MONEY_MAP_TABS.map((tab) => (
                            <View
                                key={tab.step}
                                width={48}
                                height={48}
                                borderRadius={13}
                                alignItems="center"
                                justifyContent="center"
                                backgroundColor={
                                    tab.step === step
                                        ? tab.color
                                        : 'transparent'
                                }
                                {...(isWeb ? { cursor: 'pointer' } : {})}
                                onPress={() => {
                                    if (tab.step === step) return;
                                    hasSteppedOnce.current = true;
                                    animateStep(tab.step);
                                }}
                            >
                                <Icon
                                    icon={tab.icon}
                                    size={32}
                                    color="$black"
                                />
                            </View>
                        ))}
                    </XStack>
                    <Button
                        width="auto"
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text={nextButtonText}
                        icon={
                            terminalLoading ? (
                                <Spinner
                                    size={SpinnerSize.SMALL}
                                    width="auto"
                                />
                            ) : showNextArrow ? (
                                ArrowRight
                            ) : undefined
                        }
                        iconPosition="right"
                        disabled={terminalLoading}
                        onPress={handleNext}
                    />
                </XStack>
            )}
        </>
    );
};

const FullscreenResultsContent = ({
    skipCultivating,
    onClose,
    showCloseButton = false,
    useGradientBackground = false,
    forceNarrow = false,
}: {
    skipCultivating: boolean;
    onClose?: () => void;
    showCloseButton?: boolean;
    useGradientBackground?: boolean;
    /** When true, always render the mobile/narrow layout regardless of window width. */
    forceNarrow?: boolean;
}) => {
    useClearChatPageGradient();

    const { top: safeTop } = useSafeAreaInsets();
    const topPadding = isWeb ? 0 : Math.max(safeTop, 16);
    const { width } = useWindowDimensions();
    const effectiveWidth = forceNarrow
        ? Math.min(width, WIDE_BREAKPOINT)
        : width;
    const [slide, setSlide] = useState<Slide>(Slide.AhaMoment);
    const isWideMoneyMap =
        effectiveWidth > WIDE_BREAKPOINT && slide === Slide.MoneyMap;
    const maxWidth = useMemo(() => {
        // The wide money-map is a full-bleed two-column split, so let it span
        // the whole viewport rather than capping at a centered column.
        if (isWideMoneyMap) return effectiveWidth;
        return effectiveWidth > WIDE_BREAKPOINT ? 560 : effectiveWidth;
    }, [effectiveWidth, isWideMoneyMap]);

    const [narrowExplainerActive, setNarrowExplainerActive] = useState(false);

    const { ahaResult } = useConversationalMMContext();

    // we should never be here if we came from the investing branch
    if (ahaResult.type === 'investing') return null;

    const [isCultivating, setIsCultivating] = useState(!skipCultivating);
    const [showContent, setShowContent] = useState(skipCultivating);

    useEffect(() => {
        if (skipCultivating) return;

        const holdTimer = setTimeout(() => {
            setIsCultivating(false);
        }, CULTIVATING_HOLD_MS);

        const revealTimer = setTimeout(
            () => {
                setShowContent(true);
            },
            CULTIVATING_HOLD_MS + CULTIVATING_FADE_MS / 2,
        );

        return () => {
            clearTimeout(holdTimer);
            clearTimeout(revealTimer);
        };
    }, [skipCultivating]);

    const renderSlide = () => {
        switch (slide) {
            case Slide.AhaMoment:
                return (
                    <AhaMomentSlide
                        onNext={() => {
                            setSlide(Slide.MoneyMap);
                        }}
                        useGradientBackground={useGradientBackground}
                        forceNarrow={forceNarrow}
                    />
                );
            case Slide.MoneyMap:
                return (
                    <>
                        <MoneyMapSlide
                            onBack={() => {
                                setSlide(Slide.AhaMoment);
                            }}
                            forceNarrow={forceNarrow}
                            onExplainerChange={setNarrowExplainerActive}
                            useGradientBackground={useGradientBackground}
                        />
                    </>
                );
        }
    };

    return (
        <YStack
            flex={1}
            width="100%"
            backgroundColor={
                narrowExplainerActive
                    ? '$white'
                    : useGradientBackground
                      ? undefined
                      : '$gray200'
            }
        >
            {useGradientBackground && (
                <LinearGradient
                    colors={[GRADIENT_COLOR_TOP, GRADIENT_COLOR_BOTTOM]}
                    start={[0, 0]}
                    end={[0, 1]}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                    }}
                />
            )}
            <YStack
                flex={1}
                alignSelf="center"
                alignItems="center"
                gap={32}
                justifyContent="space-between"
                width="100%"
                maxWidth={maxWidth}
                paddingTop={topPadding}
                paddingHorizontal={isWideMoneyMap ? 0 : 16}
                paddingBottom={
                    !isWideMoneyMap && isWeb && slide !== Slide.AhaMoment
                        ? 24
                        : 0
                }
            >
                {showContent && renderSlide()}
                {!skipCultivating && (
                    <CultivatingScreen visible={isCultivating} />
                )}
            </YStack>
            {showCloseButton && !narrowExplainerActive && (
                <View
                    position="absolute"
                    top={
                        effectiveWidth > WIDE_BREAKPOINT
                            ? 36
                            : isWeb
                              ? topPadding + 24
                              : topPadding + 8
                    }
                    right={effectiveWidth > WIDE_BREAKPOINT ? 36 : 16}
                    zIndex={5}
                >
                    <CloseButton background="gray" onPress={onClose} />
                </View>
            )}
        </YStack>
    );
};

export default FullscreenResultsContent;
